'use client'
import * as React from 'react'
import Link from 'next/link'
import { Check, ArrowRight, Trophy, Loader2, CheckCircle2, ShieldCheck, Search, AlertTriangle } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { trackGenerateLead } from '@/lib/analytics/ga4'
import { TurnstileWidget, useTurnstileSiteKey } from '@/lib/turnstile'
import type { HomepageAuditResult } from '@/lib/ugcHomepageAudit'

// Hybrid UGC audit. Paste a URL first: the three pillars a homepage
// fetch can actually verify (collection signals, on-site display,
// review/conversion schema) are scored server-side by
// /api/tools/ugc-audit-scan, the same SSRF-guarded engine the
// nextapp prospect-audit admin tool calls in batch. Rights management
// and measurement are process questions with nothing to detect in
// markup, so those stay as four short manual toggles layered on top.
// The final score blends both. Nothing is sent to the server beyond
// the URL scan until the merchant explicitly asks for their scorecard.

type ManualGroup = { key: string; label: string; items: { id: string; text: string }[] }

const MANUAL_GROUPS: ManualGroup[] = [
  {
    key: 'rights',
    label: 'Rights & compliance',
    items: [
      { id: 'r1', text: 'You request usage rights before reposting a customer’s content.' },
      { id: 'r2', text: 'You keep a record of who granted rights, and when.' },
    ],
  },
  {
    key: 'measurement',
    label: 'Measuring what works',
    items: [
      { id: 'm1', text: 'You can see the revenue each UGC widget brings in.' },
      { id: 'm2', text: 'You know which creators and posts drive the most sales.' },
    ],
  },
]

const MANUAL_TOTAL = MANUAL_GROUPS.reduce((n, g) => n + g.items.length, 0)

const VERDICTS = [
  { min: 0.8, label: 'UGC powerhouse', tone: 'emerald',
    note: 'Your UGC programme is in great shape. The wins now are at the margins, deeper attribution and more testing.' },
  { min: 0.55, label: 'Solid, with room to grow', tone: 'sky',
    note: 'A strong base. Close the unchecked gaps below and you’ll likely see a measurable conversion lift.' },
  { min: 0.3, label: 'Big gaps to close', tone: 'amber',
    note: 'The foundations are there, but a lot of value is leaking. Start with your lowest-scoring section.' },
  { min: 0, label: 'Leaving money on the table', tone: 'rose',
    note: 'Your customers are making content you’re not capturing or converting. This is the fastest area to improve.' },
]

const PERFECT = {
  label: 'Full marks. Genuinely rare.',
  tone: 'emerald',
  note: 'Every check ticked. There’s nothing here for us to fix, so take the victory lap: most brands never get the whole stack pulling together, and you have. Come tell us how you did it. We learn from operators running it this well, and we’ll happily trade notes on squeezing out the last few points of lift.',
} as const

const TONE: Record<string, { bg: string; text: string; bar: string }> = {
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-700', bar: 'bg-emerald-500' },
  sky: { bg: 'bg-sky-500/10', text: 'text-sky-700', bar: 'bg-sky-500' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-700', bar: 'bg-amber-600' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-700', bar: 'bg-rose-500' },
}

type Computed = {
  pct: number
  count: number
  outOf: number
  score: number
  isPerfect: boolean
  verdict: typeof VERDICTS[number] | typeof PERFECT
  tone: { bg: string; text: string; bar: string }
}

function computeScore(autoCount: number, autoOutOf: number, manualChecked: Record<string, boolean>): Computed {
  const manualCount = Object.values(manualChecked).filter(Boolean).length
  const count = autoCount + manualCount
  const outOf = autoOutOf + MANUAL_TOTAL
  const pct = outOf > 0 ? count / outOf : 0
  const isPerfect = outOf > 0 && count === outOf
  const verdict = isPerfect ? PERFECT : (VERDICTS.find((v) => pct >= v.min) ?? VERDICTS[VERDICTS.length - 1])
  return { pct, count, outOf, score: Math.round(pct * 100), isPerfect, verdict, tone: TONE[verdict.tone] }
}

export function UgcAuditChecklist() {
  const [url, setUrl] = React.useState('')
  const [scanState, setScanState] = React.useState<'idle' | 'loading' | 'ok' | 'error'>('idle')
  const [scanError, setScanError] = React.useState('')
  const [scan, setScan] = React.useState<HomepageAuditResult | null>(null)
  const [manualChecked, setManualChecked] = React.useState<Record<string, boolean>>({})

  const toggle = (id: string) => setManualChecked((c) => ({ ...c, [id]: !c[id] }))

  async function runScan(e: React.FormEvent) {
    e.preventDefault()
    if (!url.trim()) return
    setScanState('loading')
    setScanError('')
    try {
      const res = await fetch('/api/tools/ugc-audit-scan', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setScanState('error')
        setScanError(json?.error || 'Could not scan that URL. Check it and try again.')
        return
      }
      setScan(json as HomepageAuditResult)
      setScanState('ok')
    } catch {
      setScanState('error')
      setScanError('Network error scanning that URL. Try again.')
    }
  }

  const autoCount = scan?.score ?? 0
  const autoOutOf = scan?.outOf ?? 0
  const computed = computeScore(autoCount, autoOutOf, manualChecked)

  const gaps = [
    ...(scan?.rows.filter((r) => !r.passed).map((r) => `On-site: ${r.label}`) ?? []),
    ...MANUAL_GROUPS.flatMap((g) => g.items.filter((it) => !manualChecked[it.id]).map((it) => `${g.label}: ${it.text}`)),
  ]
  // Raw check ids for the failed/unchecked items, fed to the 90-day plan
  // generator via ?gaps=, which maps them onto its own gap taxonomy
  // (see lib/recommendationsEngine.ts RAW_CHECK_MAP).
  const gapIds = [
    ...(scan?.rows.filter((r) => !r.passed).map((r) => r.id) ?? []),
    ...MANUAL_GROUPS.flatMap((g) => g.items.filter((it) => !manualChecked[it.id]).map((it) => it.id)),
  ]

  return (
    <div>
      <form onSubmit={runScan} className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1">
          <Input
            type="text"
            inputMode="url"
            placeholder="yourbrand.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            aria-label="Your store URL"
            className="h-12 text-base"
          />
        </div>
        <button
          type="submit"
          disabled={scanState === 'loading' || !url.trim()}
          className="inline-flex h-12 shrink-0 items-center justify-center gap-1.5 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
        >
          {scanState === 'loading'
            ? (<><Loader2 className="h-4 w-4 animate-spin" /> Scanning…</>)
            : (<><Search className="h-4 w-4" /> Scan my site</>)}
        </button>
      </form>
      {scanState === 'error' && (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {scanError}
        </p>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        We fetch your homepage, server-side, as an anonymous shopper: no login, no JavaScript that renders after first paint. Three of the five UGC pillars show up in a page fetch; the other two (rights, measurement) are quick manual questions below once your scan is in.
      </p>

      {scan && (
        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px] lg:items-start">
          <div className="space-y-8">
            <section>
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-display text-xl font-semibold tracking-tight">
                  What we found on {new URL(scan.finalUrl).hostname}
                </h2>
                <span className="text-sm tabular-nums text-muted-foreground">{autoCount}/{autoOutOf}</span>
              </div>
              <ul className="mt-3 space-y-2">
                {scan.rows.map((r) => (
                  <li
                    key={r.id}
                    className={`flex items-start gap-3 rounded-xl border p-3.5 text-left text-sm ${
                      r.passed ? 'border-primary/40 bg-primary/5' : 'bg-card'
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                        r.passed ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'
                      }`}
                    >
                      {r.passed && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <span>
                      <span className={r.passed ? 'text-foreground' : 'text-muted-foreground'}>{r.label}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{r.evidence}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            {MANUAL_GROUPS.map((g) => {
              const groupCount = g.items.filter((it) => manualChecked[it.id]).length
              return (
                <section key={g.key}>
                  <div className="flex items-baseline justify-between gap-3">
                    <h2 className="font-display text-xl font-semibold tracking-tight">{g.label}</h2>
                    <span className="text-sm tabular-nums text-muted-foreground">{groupCount}/{g.items.length}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Not visible from a page fetch, two quick questions.</p>
                  <ul className="mt-3 space-y-2">
                    {g.items.map((it) => {
                      const on = !!manualChecked[it.id]
                      return (
                        <li key={it.id}>
                          <button
                            type="button"
                            onClick={() => toggle(it.id)}
                            aria-pressed={on}
                            className={`flex w-full items-start gap-3 rounded-xl border p-3.5 text-left text-sm transition ${
                              on ? 'border-primary/40 bg-primary/5' : 'bg-card hover:border-primary/30'
                            }`}
                          >
                            <span
                              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                                on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'
                              }`}
                            >
                              {on && <Check className="h-3.5 w-3.5" />}
                            </span>
                            <span className={on ? 'text-foreground' : 'text-muted-foreground'}>{it.text}</span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })}
          </div>

          <aside className="hidden lg:sticky lg:top-24 lg:block">
            <ScoreCard c={computed} gaps={gaps} gapIds={gapIds} url={scan.finalUrl} />
          </aside>
          <div className="lg:hidden">
            <ScoreCard c={computed} gaps={gaps} gapIds={gapIds} url={scan.finalUrl} />
          </div>
        </div>
      )}
    </div>
  )
}

function ScoreCard({ c, gaps, gapIds, url }: { c: Computed; gaps: string[]; gapIds: string[]; url: string }) {
  return (
    <div className="rounded-2xl border bg-card p-6">
      <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Your UGC score</p>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-display text-5xl font-semibold tracking-tight tabular-nums">{c.score}</span>
        <span className="text-lg text-muted-foreground">/ 100</span>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
        <div className={`h-full rounded-full transition-[width] duration-300 ${c.tone.bar}`} style={{ width: `${c.pct * 100}%` }} />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{c.count} of {c.outOf} checks in place</p>

      <div className={`mt-4 rounded-xl ${c.tone.bg} p-4`}>
        <p className={`flex items-center gap-1.5 font-display text-base font-semibold ${c.tone.text}`}>
          {c.isPerfect && <Trophy className="h-4 w-4" aria-hidden />}
          {c.verdict.label}
        </p>
        <p className="mt-1 text-sm text-foreground/80">{c.verdict.note}</p>
      </div>

      {!c.isPerfect && gapIds.length > 0 && (
        <Link
          href={`/tools/recommendations-engine?gaps=${encodeURIComponent(gapIds.join(','))}&url=${encodeURIComponent(url)}`}
          className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/40 bg-primary/5 px-4 py-2.5 text-sm font-semibold text-primary transition hover:bg-primary/10"
        >
          See your 90-day plan <ArrowRight className="h-4 w-4" />
        </Link>
      )}

      <ScoreFollowUp c={c} gaps={gaps} url={url} />
    </div>
  )
}

// Email follow-up. Captures the lead via the shared /api/leads pipeline,
// carrying the computed score + the unchecked gaps so the team can reply
// with something specific rather than a generic pitch.
function ScoreFollowUp({ c, gaps, url }: { c: Computed; gaps: string[]; url: string }) {
  const [state, setState] = React.useState<'idle' | 'submitting' | 'ok' | 'error'>('idle')
  const [error, setError] = React.useState('')
  const [token, setToken] = React.useState('')
  const siteKey = useTurnstileSiteKey()

  const intent = c.isPerfect ? 'partnership' : 'demo'

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setState('submitting')
    setError('')
    const fd = new FormData(e.currentTarget)
    const email = String(fd.get('email') || '')
    const summary =
      `UGC audit score for ${url}: ${c.score}/100 (${c.count} of ${c.outOf} checks). ` +
      `Verdict: ${c.verdict.label}.` +
      (gaps.length ? `\n\nGaps still open:\n- ${gaps.join('\n- ')}` : '\n\nA clean sweep — every check ticked.')
    const payload = {
      email,
      intent,
      source: 'ugc-audit',
      toolSlug: 'ugc-audit',
      message: summary,
      company_url: String(fd.get('company_url') || ''), // honeypot
      'cf-turnstile-response': token,
    }
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json?.ok) {
        setState('error')
        setError(json?.error || 'Something went wrong. Try again or email hi@idukki.io.')
        return
      }
      setState('ok')
      trackGenerateLead({ intent, source: 'ugc-audit' })
    } catch {
      setState('error')
      setError('Network error. Try again or email hi@idukki.io.')
    }
  }

  if (state === 'ok') {
    return (
      <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4 text-center">
        <CheckCircle2 className="mx-auto h-7 w-7 text-primary" />
        <p className="mt-2 font-display text-base font-semibold">Your scorecard is on its way.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {c.isPerfect
            ? 'A real human will reply within one business day to compare notes.'
            : 'A real human will reply within one business day with your scorecard and the quickest fixes, no bot, no drip sequence.'}
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="mt-4">
      {/* Honeypot */}
      <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden>
        <label>
          Company website
          <input type="text" name="company_url" tabIndex={-1} autoComplete="off" />
        </label>
      </div>
      <label className="block text-sm">
        <span className="font-medium text-foreground">
          {c.isPerfect ? 'Email me to compare notes' : 'Email me my scorecard + the fixes'}
        </span>
        <Input type="email" name="email" required placeholder="you@brand.com" className="mt-1.5" />
      </label>

      {siteKey && (
        <div className="mt-3">
          <p className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3" /> Bot-protection
          </p>
          <TurnstileWidget size="flexible" onToken={setToken} onExpire={() => setToken('')} />
        </div>
      )}

      <button
        type="submit"
        disabled={state === 'submitting' || (Boolean(siteKey) && !token)}
        className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
      >
        {state === 'submitting'
          ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>)
          : (<>{c.isPerfect ? 'Compare notes with our team' : 'Send me my scorecard'} <ArrowRight className="h-4 w-4" /></>)}
      </button>

      {state === 'error' && error && <p className="mt-2 text-xs text-destructive">{error}</p>}

      <p className="mt-2 text-center text-[11px] text-muted-foreground">
        We only fetch the URL you give us, and only get your email when you send it.
        {' '}Prefer to browse? <Link href="/contact" className="underline">Contact us</Link>.
      </p>
    </form>
  )
}
